import React, { Fragment, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  useImportLocationsMutation,
  useLazyGetLocationImportTemplateQuery,
} from '../../redux/api/locationApi';
import { downloadBlob } from '../../utils/download';
import { useApiErrorToast } from '../../hooks/useApiError';
import { useEventWritable } from '../../hooks/useEventWritable';

const LOCATION_TYPES = [
  { value: 'crew', label: 'Crew-Lieferziele' },
  { value: 'backstage', label: 'Backstage-/Bühnen-Lieferziele' },
  { value: 'bar', label: 'Bar-Lieferziele' },
];

const STATUS_BADGES = {
  create: { label: 'wird angelegt', cls: 'bg-success' },
  duplicate: { label: 'übersprungen', cls: 'bg-secondary' },
  error: { label: 'Fehler', cls: 'bg-danger' },
};

const ACCEPTED = '.xlsx,.xlsm,.csv';

const LocationImport = () => {
  const currentEvent = useSelector((state) => state.auth.currentEvent);
  const eventWritable = useEventWritable();
  const toastError = useApiErrorToast();
  const fileInputRef = useRef(null);

  const [locationType, setLocationType] = useState('crew');
  const [file, setFile] = useState(null);
  // The confirmed-against preview. Held in state (not RTK cache) because the
  // confirm step must send back exactly the digest this preview returned.
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const [runImport, { isLoading }] = useImportLocationsMutation();
  const [fetchTemplate, { isFetching: templateLoading }] =
    useLazyGetLocationImportTemplateQuery();

  const resetPreview = () => {
    setPreview(null);
    setResult(null);
  };

  const handleTypeChange = (value) => {
    setLocationType(value);
    resetPreview();
  };

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
    resetPreview();
  };

  const handleTemplateDownload = async () => {
    try {
      const blob = await fetchTemplate(locationType).unwrap();
      downloadBlob(blob, `lieferziel-import-vorlage-${locationType}.xlsx`);
    } catch (err) {
      toastError(err, 'Vorlage konnte nicht geladen werden.');
    }
  };

  const handlePreview = async () => {
    if (!file || !currentEvent?.id) return;
    resetPreview();
    try {
      const data = await runImport({
        eventId: currentEvent.id,
        file,
        locationType,
      }).unwrap();
      setPreview(data);
    } catch (err) {
      toastError(err, 'Die Datei konnte nicht gelesen werden.');
    }
  };

  const handleCommit = async () => {
    if (!preview || !file || !currentEvent?.id) return;
    try {
      const data = await runImport({
        eventId: currentEvent.id,
        file,
        locationType,
        commit: true,
        fileDigest: preview.file_digest,
      }).unwrap();
      setResult(data);
      setPreview(null);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success(`${data.created.length} Lieferziele angelegt.`);
    } catch (err) {
      toastError(err, 'Import fehlgeschlagen.');
    }
  };

  if (!currentEvent?.id) {
    return <p>Bitte zuerst ein Event auswählen.</p>;
  }

  const summary = preview?.summary;
  const canCommit =
    !!summary && summary.create > 0 && summary.error === 0 && eventWritable;

  return (
    <Fragment>
      <h4>Lieferziel-Import</h4>
      <p className='text-muted small'>
        Lieferziele aus einer Excel- oder CSV-Datei anlegen. Der Import legt
        immer im aktuell ausgewählten Event <strong>{currentEvent.name}</strong>{' '}
        an. Bereits vorhandene Namen werden übersprungen — nichts wird
        überschrieben.
      </p>

      <Card className='mb-4'>
        <Card.Body>
          <div className='row g-3 align-items-end'>
            <div className='col-md-4'>
              <Form.Label>Art der Lieferziele</Form.Label>
              <Form.Select
                value={locationType}
                onChange={(e) => handleTypeChange(e.target.value)}
                disabled={isLoading}
              >
                {LOCATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className='col-md-5'>
              <Form.Label>Datei (.xlsx / .csv)</Form.Label>
              <Form.Control
                type='file'
                accept={ACCEPTED}
                ref={fileInputRef}
                onChange={handleFileChange}
                disabled={isLoading}
              />
            </div>
            <div className='col-md-3 d-grid'>
              <Button
                variant='outline-success'
                onClick={handleTemplateDownload}
                disabled={templateLoading}
              >
                <i className='fas fa-file-excel me-1' />
                Beispieldatei
              </Button>
            </div>
          </div>

          <div className='d-flex gap-2 mt-3'>
            <Button
              onClick={handlePreview}
              disabled={!file || isLoading || !eventWritable}
            >
              <i className='fas fa-magnifying-glass me-1' />
              Datei prüfen
            </Button>
            {(preview || result) && (
              <Button
                variant='outline-secondary'
                onClick={() => {
                  resetPreview();
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                disabled={isLoading}
              >
                Zurücksetzen
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {result && (
        <Alert variant='success'>
          <strong>{result.created.length} Lieferziele angelegt.</strong>
          {result.created.length > 0 && (
            <div className='small mt-1'>
              {result.created.map((loc) => loc.name).join(', ')}
            </div>
          )}
        </Alert>
      )}

      {preview && (
        <Fragment>
          <Alert variant={summary.error > 0 ? 'danger' : 'info'}>
            <div>
              <strong>{summary.total}</strong> Zeile(n) gelesen —{' '}
              <strong>{summary.create}</strong> werden angelegt,{' '}
              <strong>{summary.duplicate}</strong> übersprungen,{' '}
              <strong>{summary.error}</strong> fehlerhaft.
            </div>
            {summary.error > 0 && (
              <div className='mt-1 small'>
                Solange Zeilen fehlerhaft sind, wird nichts angelegt. Bitte die
                Datei korrigieren und erneut prüfen.
              </div>
            )}
            {preview.unknown_columns.length > 0 && (
              <div className='mt-1 small'>
                Unbekannte Spalten werden ignoriert:{' '}
                {preview.unknown_columns.join(', ')}
              </div>
            )}
          </Alert>

          <Table striped hover size='sm' responsive className='align-middle'>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Zeile</th>
                <th style={{ width: 130 }}>Status</th>
                {preview.columns.map((col) => (
                  <th key={col.field}>{col.header}</th>
                ))}
                <th>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row) => (
                <tr
                  key={row.line}
                  className={
                    row.status === 'error' ? 'table-danger' : undefined
                  }
                >
                  <td>{row.line}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGES[row.status].cls}`}>
                      {STATUS_BADGES[row.status].label}
                    </span>
                  </td>
                  {preview.columns.map((col) => (
                    <td key={col.field}>{row.data[col.field] || '—'}</td>
                  ))}
                  <td className='small text-muted'>{row.errors.join(' ')}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className='d-flex justify-content-end gap-2 mb-4'>
            <Button
              variant='outline-secondary'
              onClick={resetPreview}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button
              variant='success'
              onClick={handleCommit}
              disabled={!canCommit || isLoading}
            >
              <i className='fas fa-check me-1' />
              {summary.create} Lieferziele anlegen
            </Button>
          </div>
        </Fragment>
      )}

      {!preview && !result && (
        <Card body className='text-muted small'>
          <div className='mb-2'>
            <Badge bg='light' text='dark'>
              So läuft der Import
            </Badge>
          </div>
          <ol className='mb-0 ps-3'>
            <li>Art der Lieferziele wählen und Beispieldatei herunterladen.</li>
            <li>
              Beispielzeilen ersetzen — die Spalte <strong>Name</strong> ist
              Pflicht, alle übrigen Spalten sind optional
              {locationType === 'bar' && ' (außer „Kunde“)'}.
            </li>
            <li>Datei hochladen und prüfen lassen.</li>
            <li>Ergebnis kontrollieren und den Import bestätigen.</li>
          </ol>
        </Card>
      )}
    </Fragment>
  );
};

export default LocationImport;
